import React from 'react';
import PageLoader from '../../../common/PageLoader';
import { applySort, SORT, useUserCoords } from '../../../../utils/sortListings';
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import 'bootstrap/dist/css/bootstrap.css';
import {
  getSparePartCategory,
  getSparePartListing,
} from './../../../../Redux/Actions/UserAction';
import MapModalForView from '../../../common/Modal/MapModalForView';
import { useTranslation } from 'react-i18next';
import SortControl from '../../../common/SortControl';

export default function SpareParts() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const isRTL = (i18n.language || 'en').toLowerCase().startsWith('ar');
  const L = (en, ar) => (isRTL ? ar || en : en || ar);

  const [sortOption, setSortOption] = useState(SORT.DISTANCE); // [CF 2026-08-02] nearest-first by default
  // [CF 2026-08-02] Only asks the browser for a location when the user
  // actually picks the distance sort; cached for the session after that.
  const userCoords = useUserCoords(sortOption === SORT.DISTANCE);
  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [mapModalOpen, setMapModalOpen] = useState(false);
  // #12 — filter the shop listing by spare part and/or brand
  const [partFilter, setPartFilter] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  // #15 — the brand/part filters live behind the funnel filter icon
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    dispatch(getSparePartListing());
    dispatch(getSparePartCategory());
  }, [dispatch]);

  const home = useSelector((state) => state?.home);
  const sparepartlistting = home?.SparePartList;

  // Distinct Spare Part and Brand options, derived from every shop's parts.
  const { partOptions, brandOptions } = React.useMemo(() => {
    const partMap = new Map();
    const brandMap = new Map();
    (sparepartlistting || []).forEach((shop) => {
      (shop?.parts || []).forEach((p) => {
        const part = p?.part;
        if (part?.partId && !partMap.has(part.partId)) {
          partMap.set(part.partId, L(part.name, part.nameAr) || part.name || '');
        }
        (p?.brands || []).filter(Boolean).forEach((b) => {
          if (b?.brandId && !brandMap.has(b.brandId)) {
            brandMap.set(b.brandId, L(b.name, b.nameAr) || b.name || '');
          }
        });
      });
    });
    const toSorted = (m) =>
      [...m.entries()]
        .map(([id, label]) => ({ id, label }))
        .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
    return { partOptions: toSorted(partMap), brandOptions: toSorted(brandMap) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sparepartlistting, isRTL]);

  const visibleList = React.useMemo(() => {
    if (!sparepartlistting) return undefined;
    let list = [...sparepartlistting];
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((x) =>
        (x.sparePartOwnerName || '').toLowerCase().includes(q)
      );
    }
    // Keep shops that stock the selected spare part
    if (partFilter) {
      list = list.filter((x) =>
        (x?.parts || []).some((p) => p?.part?.partId === partFilter)
      );
    }
    // Keep shops that carry the selected brand (in any of their parts)
    if (brandFilter) {
      list = list.filter((x) =>
        (x?.parts || []).some((p) =>
          (p?.brands || []).filter(Boolean).some((b) => b?.brandId === brandFilter)
        )
      );
    }
    list = applySort(list, sortOption, {
      name: (x) => x.sparePartOwnerName,
      coords: userCoords,
    });
    return list;
  }, [sparepartlistting, sortOption, userCoords, search, partFilter, brandFilter]);

  const handleOpenMap = () => {
    setMapModalOpen(true);
  };

  const handleCloseMap = () => {
    setMapModalOpen(false);
  };

  // Loading state
  if (sparepartlistting === undefined) {
    return (
      <div className="text-center text-warning" style={{ padding: '80px' }}>
        <PageLoader />
      </div>
    );
  }

  // Empty state (no spare parts at all)
  /* [CF 2026-08-09] An early `return` used to replace the WHOLE page with a
     "nothing found" panel whenever the list came back empty — the title, the
     search box and the filter panel went with it. On the server-filtered
     categories (Car Dealers, Rent a Car) that is a dead end: filter down to
     zero results and the controls you need to widen the filter have vanished,
     so the only way out is the browser Back button. The normal render path
     already shows an in-layout empty message beneath the header, so the early
     return is gone and that message does the job with the controls on screen.
  */
  return (
    <div>
      <div className="main-content-area">
        <div className="container">
          <h2 className="mb-2">{t('Spare Parts')}</h2>

          <div
            className="filter-row mb-4 d-flex align-items-center flex-wrap"
            style={{ gap: '14px' }}
          >
            {/* Sort By — left */}
            <SortControl value={sortOption} onChange={setSortOption} />

            {/* #15 — Filter funnel icon: toggles the spare-part / brand filters */}
            <button
              type="button"
              onClick={() => setFiltersOpen((o) => !o)}
              aria-label={t('Filter')}
              title={t('Filter')}
              style={{
                width: '42px',
                height: '42px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: 'none',
                borderRadius: '50%',
                background: partFilter || brandFilter ? '#fff7e6' : 'transparent',
                cursor: 'pointer',
                flex: '0 0 auto',
              }}
            >
              <img
                src="assets/images/icons/filter.png"
                alt={t('Filter')}
                style={{ width: '22px', height: '22px' }}
              />
            </button>


            {/* Search (icon → expands) + result count — fills remaining space on the same row */}
            <div
              className="d-flex align-items-center"
              style={{
                gap: '12px',
                minWidth: 0,
                flex: '1 1 auto',
                justifyContent: 'flex-end',
              }}
            >
              {searchOpen ? (
                <div
                  style={{
                    position: 'relative',
                    flex: '1 1 auto',
                    maxWidth: '460px',
                    minWidth: 0,
                  }}
                >
                  <input
                    type="text"
                    autoFocus
                    className="form-control"
                    placeholder={t('Search by name')}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ paddingInlineEnd: '34px' }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setSearch('');
                      setSearchOpen(false);
                    }}
                    aria-label={t('Clear')}
                    style={{
                      position: 'absolute',
                      insetInlineEnd: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      fontSize: '20px',
                      lineHeight: 1,
                      color: '#666',
                    }}
                  >
                    &times;
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setSearchOpen(true)}
                  aria-label={t('Search by name')}
                  style={{
                    width: '42px',
                    height: '42px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: 'none',
                    borderRadius: '50%',
                    background: 'transparent',
                    cursor: 'pointer',
                  }}
                >
                  <img src="assets/images/icons/search.png" alt={t('Search by name')} style={{ width: '22px', height: '22px' }} />
                </button>
              )}
            </div>
          </div>

          {/* [CF 2026-08-02] Same panel presentation as every other category
              (labelled fields, Reset + Apply at the bottom) — client: "how it
              appears in garage is different than in spare parts, the filter
              needs to be the same view in all". */}
          {filtersOpen && (
            <div
              className="cav-filter-panel cav-car-filter"
              style={{ textAlign: isRTL ? 'right' : 'left' }}
            >
              <div className="cav-car-filter-grid">
                <label style={{ display: 'block', minWidth: 0 }}>
                  <span className="cav-filter-label">{t('Spare Parts')}</span>
                  <select
                    value={partFilter}
                    onChange={(e) => setPartFilter(e.target.value)}
                    className="cav-filter-field"
                  >
                    <option value="">{t('All Spare Parts')}</option>
                    {partOptions.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label style={{ display: 'block', minWidth: 0 }}>
                  <span className="cav-filter-label">{t('Car Brand')}</span>
                  <select
                    value={brandFilter}
                    onChange={(e) => setBrandFilter(e.target.value)}
                    className="cav-filter-field"
                  >
                    <option value="">{t('All Brands')}</option>
                    {brandOptions.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="cav-car-filter-actions">
                <button
                  type="button"
                  className="cav-filter-reset"
                  onClick={() => {
                    setPartFilter('');
                    setBrandFilter('');
                  }}
                  disabled={!partFilter && !brandFilter}
                >
                  {t('Reset')}
                </button>
                <button
                  type="button"
                  className="cav-filter-apply"
                  onClick={() => setFiltersOpen(false)}
                >
                  {t('Apply')}
                </button>
              </div>
            </div>
          )}

          {visibleList && visibleList.length === 0 ? (
            <p className="text-muted">{t('No results match your search.')}</p>
          ) : (
            <div className="row">
              {(visibleList || []).map((x, i) => {
                const logo = x?.businessMedia?.find(
                  (media) => media?.mediaType === 'LOGO'
                )?.media?.path;

                //Location Data
                const gpsData = x?.gps;
                const lat = parseFloat((gpsData || {}).lat);
                const lng = parseFloat((gpsData || {}).long);

                return (
                  <div
                    key={x?.sparePartsId || i}
                    className="col-4 col-sm-4 col-md-4 col-lg-3 mb-4"
                  >
                    <div className="user-garage-block">
                      <div
                        className="line-a"
                        style={{
                          position: 'relative',
                          width: '100%',
                          aspectRatio: '1 / 1',
                          overflow: 'hidden',
                          borderRadius: '50%',   // [CF] client: all logos rounded
                          background: '#f4f4f4',
                        }}
                      >
                        <Link to={`/user-spare-parts-shop/${x.sparePartsId}`}>
                          <img
                            id={x.sparePartsId}
                            src={logo || 'assets/images/user/taxi/user.jpg'}
                            alt={x?.sparePartOwnerName || ''}
                            loading="lazy"
                            decoding="async"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'assets/images/user/taxi/user.jpg';
                            }}
                          />
                        </Link>
                      </div>
                      <div className="line-b">{x.sparePartOwnerName}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
